import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  TableChangeState,
  TableChangeType
} from 'components/common/Table/types';
import { Dayparts, getInitDaypart } from 'core/dayparts/Dayparts';
import {
  DeviceAvailabilityQuery,
  DeviceAvailabilityRecord,
  DeviceAvailabilitySummary,
  DeviceUnitExportQuery
} from 'core/deviceManagement/DeviceManagement';
import {
  DefaultDeviceManagementManager,
  DeviceManagementManager
} from 'core/deviceManagement/DeviceManagementManager';
import { DeviceQueryConditions, toDeviceFilterParams } from './deviceFilters';
import {
  DeviceListRequest,
  INITIAL_DEVICE_LIST_REQUEST,
  toRequestPage,
  useDeviceExport
} from './DeviceListModel';
import { resolveExportSort } from './deviceExport';
import { resolveTableSort } from './deviceListColumns';
import { toDaypartsParam } from './availabilityDayparts';
import { AVAILABILITY_DATE_FORMAT } from './availabilityFormat';

/** 走期最遠可以排到三個月後，那是業務排檔期的範圍。 */
export const AVAILABILITY_MAX_MONTHS_AHEAD = 3;

/** 預設走期：從今天起一個月。 */
export const DEFAULT_AVAILABILITY_DAYS = 30;

export type AvailabilityDateRange = {
  startDate: string;
  endDate: string;
};

export function buildDefaultDateRange(
  today: moment.Moment = moment()
): AvailabilityDateRange {
  return {
    startDate: today.clone().format(AVAILABILITY_DATE_FORMAT),
    endDate: today
      .clone()
      .add(DEFAULT_AVAILABILITY_DAYS - 1, 'days')
      .format(AVAILABILITY_DATE_FORMAT)
  };
}

export type AvailabilityRequest = DeviceListRequest &
  AvailabilityDateRange & {
    dayparts: Dayparts;
  };

export const buildInitialAvailabilityRequest = (): AvailabilityRequest => ({
  ...INITIAL_DEVICE_LIST_REQUEST,
  ...buildDefaultDateRange(),
  // 預設整週全開：一進來看到的就是「全部檔期」，縮小範圍是使用者接著做的事。
  // `getInitDaypart` 就是活動設定那邊「全天全週」的那一份，不另外做一個。
  dayparts: getInitDaypart()
});

export const toAvailabilityQuery = (
  request: AvailabilityRequest
): DeviceAvailabilityQuery => ({
  channelId: request.channelId as number,
  page: request.page,
  size: request.size,
  sortField: request.sortField,
  sortOrder: request.sortOrder,
  search: request.search,
  filter: toDeviceFilterParams(request.filter),
  storeNums: request.storeNums,
  startDate: request.startDate,
  endDate: request.endDate,
  dayparts: toDaypartsParam(request.dayparts)
});

/**
 * 匯出的查詢。
 *
 * 比清單那一份多帶走期與投放時段：可售率、可售秒數、已售秒數與日均人流是這兩個條件的
 * 函數，少了它們後端算不出那幾個值，也就排不了序。檔案本身仍然只有清單檢視那幾欄。
 */
export const toAvailabilityExportQuery = (
  request: AvailabilityRequest,
  limit?: number
): DeviceUnitExportQuery => ({
  ..._.omit(toAvailabilityQuery(request), ['page', 'size']),
  ...resolveExportSort(request.sortField, request.sortOrder),
  limit
});

const defaultManager = new DefaultDeviceManagementManager();

export const useFutureAvailabilityModel = (
  { salesChannel, filter, storeNums, active }: DeviceQueryConditions,
  manager: DeviceManagementManager = defaultManager
) => {
  const { loading, callAPIs } = useCallAPI();
  const [records, setRecords] = useState<DeviceAvailabilityRecord[]>([]);
  const [summary, setSummary] = useState<DeviceAvailabilitySummary>();
  const [totalCount, setTotalCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [request, setRequest] = useState<AvailabilityRequest>(
    buildInitialAvailabilityRequest
  );
  // 上一次真的送出去的那一組條件，換分頁回來時條件沒變就不重查。走期與投放時段
  // 也在這一組裡面，所以改過其中任何一項都會重新查。
  const lastFetched = useRef<string>();
  // 上一次看到的頁首條件，只有它真的變了才把頁碼推回第一頁。
  const lastConditions = useRef<string>();

  // 換渠道或換篩選條件就回到第一頁；留在第 5 頁很可能落在不存在的頁碼上。
  // 比的是條件的內容而不是物件本身，理由與清單那一支相同。
  useEffect(() => {
    const key = JSON.stringify({ salesChannel, filter, storeNums });
    if (lastConditions.current === key) {
      return;
    }
    lastConditions.current = key;
    setRequest((prev) => ({
      ...prev,
      channelId: salesChannel,
      filter,
      storeNums,
      page: 0
    }));
  }, [salesChannel, filter, storeNums]);

  useEffect(() => {
    if (!active || request.channelId === undefined) {
      return;
    }
    const query = toAvailabilityQuery(request);
    const key = JSON.stringify(query);
    if (lastFetched.current === key) {
      return;
    }
    lastFetched.current = key;
    callAPIs(
      [() => manager.getAvailability(query)],
      (result) => {
        setFailed(false);
        setRecords(result.records);
        setSummary(result.summary);
        setTotalCount(result.pagination.totalCount);
        setLastSyncedAt(result.lastSyncedAt);
      },
      () => {
        // 與清單那一支同一套處置（見 DeviceListModel）：失敗不算查過，舊條件的列與彙總
        // 一起清掉，並記下「查壞了」讓空畫面說得出原因。
        lastFetched.current = undefined;
        setRecords([]);
        setSummary(undefined);
        setTotalCount(0);
        setLastSyncedAt(undefined);
        setFailed(true);
      }
    );
  }, [active, request, callAPIs, manager]);

  const updateRequest = useCallback((partial: Partial<AvailabilityRequest>) => {
    setRequest((prev) => {
      const next = { ...prev, ...partial };
      return _.isEqual(prev, next) ? prev : next;
    });
  }, []);

  const onDateRangeChange = useCallback(
    (startDate?: string, endDate?: string) => {
      if (!startDate || !endDate) {
        return;
      }
      updateRequest({ startDate, endDate, page: 0 });
    },
    [updateRequest]
  );

  const onDaypartsChange = useCallback(
    (dayparts: Dayparts) => {
      updateRequest({ dayparts, page: 0 });
    },
    [updateRequest]
  );

  const onTableChange = useCallback(
    (
      type: TableChangeType,
      newState: TableChangeState<DeviceAvailabilityRecord>
    ) => {
      if (type === 'sort') {
        updateRequest({
          ...resolveTableSort(newState.sortField, newState.sortOrder),
          page: 0
        });
        return;
      }
      if (type === 'pagination') {
        updateRequest({
          page: toRequestPage(newState.page),
          size: newState.sizePerPage
        });
      }
    },
    [updateRequest]
  );

  const onHandleSearch = useCallback(
    (searchString: string) => {
      updateRequest({ search: searchString, page: 0 });
    },
    [updateRequest]
  );

  const pagination = useMemo(
    () => ({
      page: request.page + 1,
      sizePerPage: request.size,
      totalSize: totalCount
    }),
    [request.page, request.size, totalCount]
  );

  const defaultSorted = useMemo(
    () =>
      [{ dataField: request.sortField, order: request.sortOrder }] as [
        { dataField: string; order: 'asc' | 'desc' }
      ],
    // 只在第一次掛載時給表格初始排序，之後由表格自己持有。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const dateRangeBounds = useMemo(
    () => ({
      minDate: moment().format(AVAILABILITY_DATE_FORMAT),
      maxDate: moment()
        .add(AVAILABILITY_MAX_MONTHS_AHEAD, 'months')
        .format(AVAILABILITY_DATE_FORMAT)
    }),
    []
  );

  // 匯出走清單那一支端點（檔案裡沒有走期與可售統計那幾欄），但帶的是這個分頁
  // 自己的搜尋與排序 —— 兩個分頁的這兩項是分開的，借用另一邊的會匯出一份
  // 與畫面上看到的順序不同的檔案。走期與投放時段不在清單端點的條件裡，會被丟掉。
  const { exporting, exportUnits } = useDeviceExport(
    request,
    manager,
    toAvailabilityExportQuery
  );

  return {
    loading,
    records,
    summary,
    totalCount,
    lastSyncedAt,
    failed,
    request,
    pagination,
    defaultSorted,
    dateRangeBounds,
    searchString: request.search,
    onDateRangeChange,
    onDaypartsChange,
    onTableChange,
    onHandleSearch,
    exporting,
    exportUnits
  };
};

export type FutureAvailabilityModel = ReturnType<
  typeof useFutureAvailabilityModel
>;
